/**
 * Brand Configuration — Multi-tenant by hostname
 *
 * Detects the current domain and returns a brand object with:
 *  - copy (textos), planes, colores, logo, contacto, currency, payment provider
 *
 * Domains:
 *   miangelencasa.tech, www.miangelencasa.tech, *.preview.emergentagent.com  → US brand
 *   angelasis.com, www.angelasis.com, mx.miangelencasa.tech                  → MX brand
 */

const US_BRAND = {
  id: "us",
  name: "Mi Ángel en Casa",
  shortName: "Mi Ángel",
  domain: "miangelencasa.tech",
  tagline: "en Casa",
  logo: "/angel-logo-white.png",
  logoIcon: "/angel-512.png",
  paymentProvider: "stripe",
  currency: "USD",
  currencySymbol: "$",
  locale: "es-MX",
  // Add-on: Health-scan digital consultations (Anura)
  healthScanAddOn: {
    id: "health_scan",
    name: "Consulta médica digital",
    pricePerMonth: 1,           // +$1 USD/mes
    currency: "USD",
    extraScanPrice: 3,           // $3 USD por escaneo adicional
    scansPerPerson: 4,           // 4 escaneos/año por persona en Individual
    scansFamilyPerPerson: 1,     // 1 escaneo/año por persona en Familiar (4 personas = 4 total)
    description: "Mide tu salud desde el celular: presión, ritmo cardíaco, estrés y más. 4 escaneos al año por persona.",
  },
  whatsapp: "https://wa.me/525536964048?text=Hola%20Me%20interesa%20obtener%20informaci%C3%B3n",
  contactPhone: "+52 55 3696 4048",
  contactEmail: "info@miangelencasa.tech",
  contactCity: "Ciudad de México, México",
  // Theme: navy primary, lime accent (current default)
  theme: {
    heroBackground: "linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)",
    heroTextColor: "#0f172a",
    primaryColor: "#163680",
    accentColor: "#7DC242",
  },
  seo: {
    title: "Mi Ángel en Casa — Servicio Vial, Médico y Legal 24/7 en México",
    description: "Tu Ángel cuida a los tuyos en México mientras trabajas en EU. Servicio vial, médico, legal y para el hogar — disponible 24/7 en toda la república.",
    canonical: "https://miangelencasa.tech/",
  },
  hero: {
    title: "Estás lejos. Nosotros estamos cerca de los tuyos.",
    subtitle: "Mientras trabajas en Estados Unidos, nosotros cuidamos a tu familia en México. Servicio vial, médico, legal y para el hogar — disponible 24/7 en toda la república.",
    primaryCta: "Proteger a mi Familia",
    photoUrl: "https://images.pexels.com/photos/7901616/pexels-photo-7901616.jpeg?auto=compress&cs=tinysrgb&w=1000",
    photoAlt: "Campesino mexicano abrazando a su mamá en el campo al atardecer",
    photoBadgeTitle: "Allá están bien.",
    photoBadgeSub: "Tu Ángel los cuida.",
  },
  razon: {
    badge: "Nuestra razón de ser",
    titleA: "Cuando tu familia te necesita,",
    titleHighlight: "tú no puedes estar ahí.",
    titleB: " Nosotros sí.",
    paragraphs: [
      "Estás del otro lado de la frontera, trabajando duro por los tuyos. Pero a tres mil kilómetros de distancia, la vida sigue: una llanta ponchada en plena carretera, una urgencia médica de madrugada, un trámite legal que nadie sabe cómo resolver…",
      "Y tú no puedes pedir permiso en el trabajo, no puedes tomar un vuelo, no puedes estar en dos lugares a la vez. <strong>Pero puedes mandar a tu Ángel.</strong>",
      "Con un solo plan, tu mamá, tu papá, tu esposa, tus hijos en México tienen <strong>grúa, ambulancia, doctor y abogado</strong> a una llamada de distancia. Tú duermes tranquilo. Ellos saben que no están solos.",
    ],
    photoUrl: "https://images.pexels.com/photos/8961155/pexels-photo-8961155.jpeg?auto=compress&cs=tinysrgb&w=900",
    photoAlt: "Trabajador mexicano contento en su jornada en Estados Unidos",
    photoQuote: "Trabajo tranquilo aquí, sabiendo que allá los míos están bien cuidados.",
    photoAuthor: "— Miguel, Texas",
    ctaButton: "Quiero proteger a mi familia",
    minis: [
      { icon: "fas fa-truck-pickup", title: "Se quedaron varados.", sub: "Mandamos la grúa." },
      { icon: "fas fa-ambulance", title: "Hay una urgencia.", sub: "Despachamos ambulancia." },
      { icon: "fas fa-stethoscope", title: "Se sienten mal.", sub: "Doctor por teléfono al instante." },
      { icon: "fas fa-gavel", title: "Necesitan un abogado.", sub: "Apoyo legal en minutos." },
    ],
  },
  features: [
    { icon: "fas fa-stethoscope", title: "Médico Telefónico", desc: "Orientación médica 24/7 con doctores certificados sin salir de casa" },
    { icon: "fas fa-gavel", title: "Apoyo Legal", desc: "Asesoría legal telefónica y acompañamiento ante accidentes, trámites o emergencias jurídicas" },
    { icon: "fas fa-clock", title: "Disponibilidad 24/7", desc: "Activamos tu servicio los 365 días del año, a cualquier hora" },
    { icon: "fas fa-bolt", title: "Respuesta Inmediata", desc: "Despachamos grúa, ambulancia o asesor legal en minutos desde tu llamada" },
    { icon: "fas fa-mobile-alt", title: "Solicita desde tu Móvil", desc: "Gestiona y activa todos tus servicios desde nuestra plataforma fácil de usar" },
    { icon: "fas fa-headset", title: "Soporte Dedicado", desc: "Línea de atención multicanal: teléfono, chat y WhatsApp" },
  ],
  servicios: [
    { icon: "fas fa-truck-pickup", title: "Servicio Vial / Grúa", desc: "Servicio de grúa, paso de corriente, cambio de llanta y suministro de combustible cuando lo necesites" },
    { icon: "fas fa-stethoscope", title: "Médico Telefónico", desc: "Orientación médica 24/7 con doctores certificados desde la comodidad de tu hogar" },
    { icon: "fas fa-ambulance", title: "Envío de Ambulancia", desc: "Despacho de ambulancia ante una emergencia médica con cobertura en toda la república" },
    { icon: "fas fa-gavel", title: "Apoyo Legal", desc: "Asesoría legal telefónica y acompañamiento ante accidentes, trámites o emergencias jurídicas" },
  ],
  planes: [
    {
      id: "basico", nombre: "Plan Básico", desc: "Para comenzar a estar protegido",
      precio: "4.99", periodo: "/mes",
      precioAnualEquivalente: "4.16",
      promoBadge: "AHORRA 2 MESES",
      promoNota: "Pagado anual: $49.90/año (equivale a $4.16/mes) — te ahorras 2 meses",
      features: [
        { ok: true, text: "1 servicio de grúa al año" },
        { ok: true, text: "Médico telefónico" },
        { ok: true, text: "Soporte por chat" },
        { ok: false, text: "Envío de ambulancia" },
        { ok: false, text: "Apoyo legal" },
      ],
    },
    {
      id: "estandar", nombre: "Plan Estándar", desc: "Protege a toda tu familia",
      precio: "9.99", periodo: "/mes", featured: true,
      precioAnualEquivalente: "8.32",
      promoBadge: "AHORRA 2 MESES",
      promoNota: "Pagado anual: $99.90/año (equivale a $8.32/mes) — te ahorras 2 meses",
      features: [
        { ok: true, text: "Incluye hasta 4 personas" },
        { ok: true, text: "3 servicios de grúa al año" },
        { ok: true, text: "Médico 24/7" },
        { ok: true, text: "Envío de ambulancia (1 al año)" },
        { ok: true, text: "Apoyo legal" },
        { ok: true, text: "Soporte prioritario telefónico" },
      ],
    },
  ],
  testimonios: [
    { nombre: "María García", ciudad: "Ciudad de México", texto: "Se me ponchó una llanta en plena carretera y la grúa llegó en 25 minutos. Excelente servicio, muy profesionales." },
    { nombre: "Carlos López", ciudad: "Guadalajara", texto: "Tuvimos una emergencia médica de madrugada y la ambulancia llegó rapidísimo. Nos dieron tranquilidad cuando más la necesitábamos." },
    { nombre: "Patricia Ruiz", ciudad: "Monterrey", texto: "El apoyo legal me ayudó muchísimo tras un choque vial. Resolvieron todo el trámite sin que yo tuviera que ir a ninguna oficina." },
  ],
};

const MX_BRAND = {
  id: "mx",
  name: "Mi Ángel Asis",
  shortName: "Mi Ángel",
  domain: "angelasis.com",
  tagline: "Asis",
  logo: "/angel-logo-white.png", // se verá blanco sobre fondo azul
  logoIcon: "/angel-512.png",
  paymentProvider: "mercadopago",
  currency: "MXN",
  currencySymbol: "$",
  locale: "es-MX",
  // Add-on: Health-scan digital consultations (Anura)
  healthScanAddOn: {
    id: "health_scan",
    name: "Consulta médica digital",
    pricePerMonth: 10,           // +$10 MXN/mes
    currency: "MXN",
    extraScanPrice: 60,           // ~$3 USD = $60 MXN por escaneo adicional
    scansPerPerson: 4,            // 4 escaneos/año por persona en Individual
    scansFamilyPerPerson: 1,      // 1 escaneo/año por persona en Familiar (4 personas = 4 total)
    description: "Mide tu salud desde el celular: presión, ritmo cardíaco, estrés y más. 4 escaneos al año por persona.",
  },
  whatsapp: "https://wa.me/525536964048?text=Hola%20Quiero%20info%20de%20Mi%20%C3%81ngel%20Asis",
  contactPhone: "+52 55 3696 4048",
  contactEmail: "hola@angelasis.com",
  contactCity: "Ciudad de México, México",
  // Theme: navy primary as background (azul fondo), white text
  theme: {
    heroBackground: "linear-gradient(135deg, #0c1f4a 0%, #163680 60%, #1e4ab5 100%)",
    heroTextColor: "#ffffff",
    primaryColor: "#163680",
    accentColor: "#FFD700",
  },
  seo: {
    title: "Mi Ángel Asis — Grúa, Ambulancia, Médico y Legal 24/7",
    description: "¿Recuerdas cuándo pagaste $3,500 por una grúa? Con Mi Ángel Asis, por $80 al mes tienes grúa, ambulancia, médico y apoyo legal cuando los necesites. Solo el club de descuentos paga tu membresía.",
    canonical: "https://angelasis.com/",
  },
  hero: {
    title: "Una emergencia no avisa. Tu Ángel sí responde.",
    subtitle: "Grúa, ambulancia, médico en línea, apoyo legal y un club de descuentos que paga tu membresía. Por menos de lo que cuesta una pizza al mes.",
    primaryCta: "Quiero mi Ángel ya",
    photoUrl: "https://images.unsplash.com/photo-1518105779142-d975f22f1b0a?w=1000&q=80&auto=format&fit=crop",
    photoAlt: "Calle urbana mexicana de día con arquitectura colonial y vida diaria",
    photoBadgeTitle: "Aquí estamos.",
    photoBadgeSub: "Tu Ángel responde en minutos.",
  },
  razon: {
    badge: "¿Te suena familiar?",
    titleA: "La última vez que pagaste una grúa",
    titleHighlight: "te costó $3,500 pesos.",
    titleB: " Esto vale $80.",
    paragraphs: [
      "Ese día la batería murió en el estacionamiento del súper. O fue la llanta ponchada en el Periférico. O tu mamá amaneció mal y no sabías a quién hablarle. Y tuviste que sacar la cartera, pedir prestado, o aguantarte el coraje.",
      "<strong>Eso ya no tiene por qué pasarte otra vez.</strong> Por $80 al mes — menos que un café con leche a la semana — tienes a tu Ángel listo: grúa, ambulancia, médico en línea, apoyo legal y un club de descuentos que en muchos casos paga tu membresía solo con lo que ahorras.",
      "<strong>Una llamada. Llegamos.</strong> Sin trámites eternos, sin letras chiquitas. Mi Ángel Asis vino a cuidar a tu familia como tú lo harías si pudieras estar en todos lados a la vez.",
    ],
    photoUrl: "https://images.unsplash.com/photo-1444723121867-7a241cacace9?w=900&q=80&auto=format&fit=crop",
    photoAlt: "Ciudad urbana de noche iluminada — cuando las emergencias no avisan",
    photoQuote: "Antes pagaba $2,800 cada vez que la batería me fallaba. Ahora con $80 al mes tengo paz mental.",
    photoAuthor: "— Roberto, CDMX",
    ctaButton: "Quiero a mi Ángel cuidándome",
    minis: [
      { icon: "fas fa-truck-pickup", title: "Se te descompone el coche.", sub: "Te mandamos una grúa." },
      { icon: "fas fa-ambulance", title: "Emergencia en casa.", sub: "Despachamos la ambulancia." },
      { icon: "fas fa-stethoscope", title: "Te sientes mal.", sub: "Doctor en línea sin salir de casa." },
      { icon: "fas fa-gavel", title: "Necesitas un abogado.", sub: "Apoyo legal al teléfono." },
      { icon: "fas fa-tags", title: "Compras todos los días.", sub: "Club de descuentos en farmacias, restaurantes y cines." },
    ],
  },
  features: [
    { icon: "fas fa-ambulance", title: "Ambulancia 24/7", desc: "Despacho de ambulancia ante emergencias médicas. Cobertura en toda la república." },
    { icon: "fas fa-stethoscope", title: "Médico en Línea", desc: "Doctor certificado a una llamada o videollamada. Sin filas, sin esperas, a cualquier hora." },
    { icon: "fas fa-gavel", title: "Apoyo Legal", desc: "Asesoría legal telefónica para accidentes, trámites, deudas o cualquier bronca jurídica." },
    { icon: "fas fa-tags", title: "Club de Descuentos", desc: "Farmacias, restaurantes, talleres, 2x1 en cines. Lo que ahorras paga tu membresía sola." },
    { icon: "fas fa-mobile-alt", title: "Desde tu Celular", desc: "Activas y solicitas todo desde la app. Sin papeles, sin oficinas, sin perder tiempo." },
  ],
  servicios: [
    { icon: "fas fa-truck-pickup", title: "Grúa", desc: "Servicio de grúa hasta dos eventos al año." },
    { icon: "fas fa-ambulance", title: "Ambulancia", desc: "Despacho de ambulancia ante una emergencia médica con cobertura nacional." },
    { icon: "fas fa-stethoscope", title: "Médico en Línea", desc: "Orientación médica 24/7 por teléfono o video con doctores certificados." },
    { icon: "fas fa-gavel", title: "Apoyo Legal", desc: "Asesoría legal telefónica y acompañamiento ante accidentes y trámites." },
    { icon: "fas fa-tags", title: "Club de Descuentos", desc: "Beneficios en farmacias, restaurantes, talleres mecánicos y 2x1 en cines. Solo con los descuentos, tu membresía se paga sola." },
  ],
  planes: [
    {
      id: "mx_individual", nombre: "Plan Individual", desc: "Para ti. Tranquilidad total.",
      precio: "80", periodo: "/mes",
      precioRegular: "80", promoBadge: "PAGA ANUAL Y AHORRA",
      promoNota: "Pagado de forma anual: $468/año (equivale a $39/mes)",
      precioAnualEquivalente: "39",
      features: [
        { ok: true, text: "1 persona protegida" },
        { ok: true, text: "Ambulancia ante emergencias" },
        { ok: true, text: "Médico en línea 24/7" },
        { ok: true, text: "Apoyo legal telefónico" },
        { ok: true, text: "Club de descuentos" },
      ],
    },
    {
      id: "mx_familiar", nombre: "Plan Familiar", desc: "Hasta 4 personas. La paz de toda tu familia.",
      precio: "180", periodo: "/mes", featured: true,
      precioRegular: "180", promoBadge: "ANUAL + 2 MESES GRATIS",
      promoNota: "Pagado de forma anual: $948/año (equivale a $79/mes) + 2 meses gratis",
      precioAnualEquivalente: "79",
      features: [
        { ok: true, text: "Hasta 4 personas protegidas" },
        { ok: true, text: "Ambulancia ante emergencias" },
        { ok: true, text: "Médico en línea 24/7 para todos" },
        { ok: true, text: "Apoyo legal telefónico" },
        { ok: true, text: "Club de descuentos para toda la familia" },
        { ok: true, text: "2 meses GRATIS pagando anual" },
      ],
    },
  ],
  testimonios: [
    { nombre: "Roberto Hernández", ciudad: "Ciudad de México", texto: "Se me quedó la batería en plaza Antara un sábado. La grúa llegó en 18 minutos. Antes me cobraban $2,800. Ahora pago $80 al mes y la duermo tranquila." },
    { nombre: "Laura Mendoza", ciudad: "Guadalajara", texto: "Mi hija se accidentó en la moto. La ambulancia llegó rapidísimo y nos cubrieron todo. No saben el peso que se nos quitó de encima." },
    { nombre: "Jorge Ramírez", ciudad: "Monterrey", texto: "Con el club de descuentos, en farmacias del Ahorro me ahorro como $400 al mes en medicamentos de mi mamá. La membresía se paga sola." },
  ],
};

/**
 * Returns the brand config matching the current hostname.
 * Defaults to US brand when hostname is unknown.
 *
 * Override via ?brand=mx for testing in preview, or localStorage.setItem('brand_override', 'mx').
 */
export function getBrand() {
  if (typeof window === "undefined") return US_BRAND;

  // Override #0: build-time env var (production — each deploy forces its brand)
  const envBrand = (process.env.REACT_APP_BRAND || "").toLowerCase().trim();
  if (envBrand === "mx") return MX_BRAND;
  if (envBrand === "us") return US_BRAND;

  // Override #1: URL query param ?brand=mx (highest priority, for testing)
  try {
    const params = new URLSearchParams(window.location.search);
    const qbrand = params.get("brand");
    if (qbrand === "mx") {
      try { window.localStorage.setItem("brand_override", "mx"); } catch (e) { /* ignore */ }
      return MX_BRAND;
    }
    if (qbrand === "us") {
      try { window.localStorage.removeItem("brand_override"); } catch (e) { /* ignore */ }
      return US_BRAND;
    }
  } catch (e) { /* ignore */ }

  // Override #2: localStorage (persists across navigation in preview)
  try {
    if (window.localStorage.getItem("brand_override") === "mx") return MX_BRAND;
  } catch (e) { /* ignore */ }

  // Default: hostname-based detection (production behavior)
  const host = window.location.hostname.toLowerCase();
  if (host.includes("angelasis") || host.startsWith("mx.")) return MX_BRAND;

  // Auto-detect: if visitor's timezone is in Mexico OR language is Spanish → MX brand
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("Mexico") || tz.includes("Tijuana") || tz.includes("Cancun") ||
        tz.includes("Monterrey") || tz.includes("Merida") || tz.includes("Chihuahua") ||
        tz.includes("Hermosillo") || tz.includes("Mazatlan") || tz.includes("Ojinaga") ||
        tz.includes("Matamoros") || tz.includes("Bahia_Banderas")) {
      return MX_BRAND;
    }
    const lang = (navigator.language || (navigator.languages && navigator.languages[0]) || "").toLowerCase();
    // Spanish speakers (except es-ES from Spain) default to MX brand
    if (lang.startsWith("es") && !lang.startsWith("es-es")) {
      return MX_BRAND;
    }
  } catch (e) { /* ignore */ }

  return US_BRAND;
}

export { US_BRAND, MX_BRAND };
